/**
 * DateTimeBox - Labeled datetime display box.
 *
 * Shows a labeled (Start/End) date and time in a bordered box.
 * Used by MeetingCardContent and CalendarEventCard.
 */

import { cx } from '@/utils/cx';

export interface DateTimeBoxProps {
  /** Label displayed above the date (e.g., "Start", "End") */
  label: string;
  /** Formatted date string (e.g., "Thu, Mar 30") */
  date: string;
  /** Formatted time string (e.g., "2:00 PM") */
  time: string;
  /** When provided, makes the box clickable (e.g. to open a picker) */
  onClick?: () => void;
  className?: string;
}

export function DateTimeBox({
  label,
  date,
  time,
  onClick,
  className,
}: DateTimeBoxProps) {
  const isClickable = !!onClick;

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cx(
        'flex-1 min-w-[120px] rounded-lg border border-[var(--color-gray-300)] shadow-[var(--shadow-xs)] bg-bg-primary px-3 py-2',
        isClickable &&
          'cursor-pointer hover:border-brand-300 hover:shadow-[var(--shadow-xs-focused-4px-brand-100)] transition-all',
        className
      )}
    >
      <p className='text-xs font-normal text-fg-primary tracking-wide mb-0.5'>
        {label}
      </p>
      <p className='text-sm font-medium text-fg-tertiary'>
        {date ? (
          <>
            {date} {time && `• ${time}`}
          </>
        ) : (
          'Not set'
        )}
      </p>
    </div>
  );
}
